import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
  createSelector,
} from '@reduxjs/toolkit';
import type {
  ContextualTrigger,
  SmartNotification,
  AINotificationType,
  AIError,
} from '@coffeeplexity/shared';
import {
  contextualNotificationTriggersService,
  type BrewingReminderConfig,
  type MilestoneAchievementData,
  type ReEngagementConfig,
  type LearningReminderSchedule,
  type ABTestVariant,
  type NotificationDeliveryOptimization,
} from '../../services/contextualNotificationTriggers';
import type { RootState } from '../index';

interface ContextualTriggersState {
  brewingReminders: ContextualTrigger[];
  learningReminders: ContextualTrigger[];
  reEngagementTriggers: ContextualTrigger[];
  milestoneNotifications: SmartNotification[];
  abTestVariants: ABTestVariant[];
  activeABTests: Array<{
    testId: string;
    userId: string;
    variant: ABTestVariant;
    startDate: string;
    status: 'running' | 'completed' | 'paused';
  }>;
  deliveryOptimizations: NotificationDeliveryOptimization[];
  loading: {
    brewingReminders: boolean;
    learningReminders: boolean;
    reEngagement: boolean;
    milestones: boolean;
    abTesting: boolean;
    analytics: boolean;
  };
  error: AIError | null;
  lastUpdated: string | null;
  analytics: {
    totalTriggers: number;
    activeTriggers: number;
    engagementRate: number;
    abTestResults: any[];
  };
}

const initialState: ContextualTriggersState = {
  brewingReminders: [],
  learningReminders: [],
  reEngagementTriggers: [],
  milestoneNotifications: [],
  abTestVariants: [],
  activeABTests: [],
  deliveryOptimizations: [],
  loading: {
    brewingReminders: false,
    learningReminders: false,
    reEngagement: false,
    milestones: false,
    abTesting: false,
    analytics: false,
  },
  error: null,
  lastUpdated: null,
  analytics: {
    totalTriggers: 0,
    activeTriggers: 0,
    engagementRate: 0,
    abTestResults: [],
  },
};

// Async thunks
export const createBrewingReminder = createAsyncThunk(
  'contextualTriggers/createBrewingReminder',
  async (config: BrewingReminderConfig, { rejectWithValue }) => {
    try {
      return await contextualNotificationTriggersService.createBrewingReminder(
        config
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const createMilestoneNotification = createAsyncThunk(
  'contextualTriggers/createMilestoneNotification',
  async (data: MilestoneAchievementData, { rejectWithValue }) => {
    try {
      return await contextualNotificationTriggersService.createMilestoneAchievementNotification(
        data
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const createReEngagementNotification = createAsyncThunk(
  'contextualTriggers/createReEngagementNotification',
  async (config: ReEngagementConfig, { rejectWithValue }) => {
    try {
      return await contextualNotificationTriggersService.createReEngagementNotification(
        config
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const createLearningReminder = createAsyncThunk(
  'contextualTriggers/createLearningReminder',
  async (schedule: LearningReminderSchedule, { rejectWithValue }) => {
    try {
      return await contextualNotificationTriggersService.createContextualLearningReminder(
        schedule
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const optimizeNotificationDelivery = createAsyncThunk(
  'contextualTriggers/optimizeDelivery',
  async (
    {
      userId,
      notificationType,
    }: { userId: string; notificationType: AINotificationType },
    { rejectWithValue }
  ) => {
    try {
      return await contextualNotificationTriggersService.optimizeNotificationDelivery(
        userId,
        notificationType
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const recordABTestResult = createAsyncThunk(
  'contextualTriggers/recordABTestResult',
  async (
    optimization: NotificationDeliveryOptimization,
    { rejectWithValue }
  ) => {
    try {
      await contextualNotificationTriggersService.recordABTestResult(
        optimization
      );
      return optimization;
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

export const fetchABTestAnalytics = createAsyncThunk(
  'contextualTriggers/fetchABTestAnalytics',
  async (
    {
      testId,
      timeRange,
    }: { testId?: string; timeRange?: 'day' | 'week' | 'month' },
    { rejectWithValue }
  ) => {
    try {
      return await contextualNotificationTriggersService.getABTestAnalytics(
        testId,
        timeRange
      );
    } catch (error: any) {
      return rejectWithValue(error as AIError);
    }
  }
);

const contextualTriggersSlice = createSlice({
  name: 'contextualTriggers',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearTriggers: state => {
      state.brewingReminders = [];
      state.learningReminders = [];
      state.reEngagementTriggers = [];
      state.milestoneNotifications = [];
      state.lastUpdated = null;
    },
    updateTriggerStatus: (
      state,
      action: PayloadAction<{ triggerId: string; enabled: boolean }>
    ) => {
      const { triggerId, enabled } = action.payload;

      // Update in all trigger arrays
      [
        state.brewingReminders,
        state.learningReminders,
        state.reEngagementTriggers,
      ].forEach(triggers => {
        const trigger = triggers.find(t => t.id === triggerId);
        if (trigger) {
          trigger.enabled = enabled;
        }
      });
    },
    removeTrigger: (state, action: PayloadAction<string>) => {
      const triggerId = action.payload;

      state.brewingReminders = state.brewingReminders.filter(
        t => t.id !== triggerId
      );
      state.learningReminders = state.learningReminders.filter(
        t => t.id !== triggerId
      );
      state.reEngagementTriggers = state.reEngagementTriggers.filter(
        t => t.id !== triggerId
      );
    },
    addDeliveryOptimization: (
      state,
      action: PayloadAction<NotificationDeliveryOptimization>
    ) => {
      state.deliveryOptimizations.unshift(action.payload);
      // Keep only the most recent 100 optimizations
      if (state.deliveryOptimizations.length > 100) {
        state.deliveryOptimizations = state.deliveryOptimizations.slice(0, 100);
      }
    },
    updateABTestStatus: (
      state,
      action: PayloadAction<{
        testId: string;
        status: 'running' | 'completed' | 'paused';
      }>
    ) => {
      const { testId, status } = action.payload;
      const test = state.activeABTests.find(t => t.testId === testId);
      if (test) {
        test.status = status;
      }
    },
    markMilestoneNotificationSeen: (state, action: PayloadAction<string>) => {
      const notificationId = action.payload;
      const notification = state.milestoneNotifications.find(
        n => n.id === notificationId
      );
      if (notification) {
        notification.metadata = {
          ...notification.metadata,
          seen: true,
          seenAt: new Date().toISOString(),
        };
      }
    },
    updateAnalytics: (
      state,
      action: PayloadAction<Partial<ContextualTriggersState['analytics']>>
    ) => {
      state.analytics = {
        ...state.analytics,
        ...action.payload,
      };
    },
  },
  extraReducers: builder => {
    // Create Brewing Reminder
    builder
      .addCase(createBrewingReminder.pending, state => {
        state.loading.brewingReminders = true;
        state.error = null;
      })
      .addCase(createBrewingReminder.fulfilled, (state, action) => {
        state.loading.brewingReminders = false;
        state.brewingReminders.push(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createBrewingReminder.rejected, (state, action) => {
        state.loading.brewingReminders = false;
        state.error = action.payload as AIError;
      });

    // Create Milestone Notification
    builder
      .addCase(createMilestoneNotification.pending, state => {
        state.loading.milestones = true;
        state.error = null;
      })
      .addCase(createMilestoneNotification.fulfilled, (state, action) => {
        state.loading.milestones = false;
        state.milestoneNotifications.push(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createMilestoneNotification.rejected, (state, action) => {
        state.loading.milestones = false;
        state.error = action.payload as AIError;
      });

    // Create Re-engagement Notification
    builder
      .addCase(createReEngagementNotification.pending, state => {
        state.loading.reEngagement = true;
        state.error = null;
      })
      .addCase(createReEngagementNotification.fulfilled, (state, action) => {
        state.loading.reEngagement = false;
        // Store as a trigger for future reference
        const trigger: ContextualTrigger = {
          id: action.payload.id,
          name: 'Re-engagement Notification',
          condition: {
            type: 'reengagement',
            notificationId: action.payload.id,
          },
          action: {
            type: 'send_notification',
            template: 'reengagement',
            personalization: action.payload.metadata,
          },
          enabled: true,
          priority: 2,
          cooldownMinutes: 2880, // 48 hours
        };
        state.reEngagementTriggers.push(trigger);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createReEngagementNotification.rejected, (state, action) => {
        state.loading.reEngagement = false;
        state.error = action.payload as AIError;
      });

    // Create Learning Reminder
    builder
      .addCase(createLearningReminder.pending, state => {
        state.loading.learningReminders = true;
        state.error = null;
      })
      .addCase(createLearningReminder.fulfilled, (state, action) => {
        state.loading.learningReminders = false;
        state.learningReminders.push(action.payload);
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(createLearningReminder.rejected, (state, action) => {
        state.loading.learningReminders = false;
        state.error = action.payload as AIError;
      });

    // Optimize Notification Delivery
    builder
      .addCase(optimizeNotificationDelivery.pending, state => {
        state.loading.abTesting = true;
        state.error = null;
      })
      .addCase(optimizeNotificationDelivery.fulfilled, (state, action) => {
        state.loading.abTesting = false;
        const { testId, variant } = action.payload;

        // Add to active A/B tests
        state.activeABTests.push({
          testId,
          userId: '', // Would be filled from action meta
          variant,
          startDate: new Date().toISOString(),
          status: 'running',
        });

        state.lastUpdated = new Date().toISOString();
      })
      .addCase(optimizeNotificationDelivery.rejected, (state, action) => {
        state.loading.abTesting = false;
        state.error = action.payload as AIError;
      });

    // Record A/B Test Result
    builder
      .addCase(recordABTestResult.pending, state => {
        state.loading.abTesting = true;
      })
      .addCase(recordABTestResult.fulfilled, (state, action) => {
        state.loading.abTesting = false;
        state.deliveryOptimizations.unshift(action.payload);

        // Update analytics
        const totalOptimizations = state.deliveryOptimizations.length;
        const engagedOptimizations = state.deliveryOptimizations.filter(
          opt => opt.opened || opt.clicked
        ).length;

        state.analytics.engagementRate =
          totalOptimizations > 0
            ? engagedOptimizations / totalOptimizations
            : 0;
      })
      .addCase(recordABTestResult.rejected, (state, action) => {
        state.loading.abTesting = false;
        state.error = action.payload as AIError;
      });

    // Fetch A/B Test Analytics
    builder
      .addCase(fetchABTestAnalytics.pending, state => {
        state.loading.analytics = true;
        state.error = null;
      })
      .addCase(fetchABTestAnalytics.fulfilled, (state, action) => {
        state.loading.analytics = false;
        state.analytics.abTestResults = action.payload.tests;
        state.analytics.totalTriggers = action.payload.summary.totalTests;
        state.analytics.activeTriggers = action.payload.summary.activeTests;
      })
      .addCase(fetchABTestAnalytics.rejected, (state, action) => {
        state.loading.analytics = false;
        state.error = action.payload as AIError;
      });
  },
});

export const {
  clearError,
  clearTriggers,
  updateTriggerStatus,
  removeTrigger,
  addDeliveryOptimization,
  updateABTestStatus,
  markMilestoneNotificationSeen,
  updateAnalytics,
} = contextualTriggersSlice.actions;

// Basic selectors
export const selectBrewingReminders = (state: RootState) =>
  state.contextualTriggers.brewingReminders;
export const selectLearningReminders = (state: RootState) =>
  state.contextualTriggers.learningReminders;
export const selectReEngagementTriggers = (state: RootState) =>
  state.contextualTriggers.reEngagementTriggers;
export const selectMilestoneNotifications = (state: RootState) =>
  state.contextualTriggers.milestoneNotifications;
export const selectActiveABTests = (state: RootState) =>
  state.contextualTriggers.activeABTests;
export const selectDeliveryOptimizations = (state: RootState) =>
  state.contextualTriggers.deliveryOptimizations;
export const selectContextualTriggersLoading = (state: RootState) =>
  state.contextualTriggers.loading;
export const selectContextualTriggersError = (state: RootState) =>
  state.contextualTriggers.error;
export const selectContextualTriggersAnalytics = (state: RootState) =>
  state.contextualTriggers.analytics;

// Memoized selectors
export const selectAllActiveTriggers = createSelector(
  [selectBrewingReminders, selectLearningReminders, selectReEngagementTriggers],
  (brewingReminders, learningReminders, reEngagementTriggers) => {
    return [
      ...brewingReminders.filter(t => t.enabled),
      ...learningReminders.filter(t => t.enabled),
      ...reEngagementTriggers.filter(t => t.enabled),
    ];
  }
);

export const selectTriggersByPriority = createSelector(
  [selectAllActiveTriggers],
  triggers => {
    return triggers.sort((a, b) => b.priority - a.priority);
  }
);

export const selectUnseenMilestoneNotifications = createSelector(
  [selectMilestoneNotifications],
  notifications => {
    return notifications.filter(n => !n.metadata?.seen);
  }
);

export const selectRunningABTests = createSelector(
  [selectActiveABTests],
  tests => {
    return tests.filter(test => test.status === 'running');
  }
);

export const selectRecentDeliveryOptimizations = createSelector(
  [selectDeliveryOptimizations],
  optimizations => {
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
    return optimizations.filter(opt => {
      // Assuming deliveryTime is the timestamp
      return new Date(opt.deliveryTime) > oneDayAgo;
    });
  }
);

export const selectEngagementRateByType = createSelector(
  [selectDeliveryOptimizations],
  optimizations => {
    const typeStats: Record<string, { total: number; engaged: number }> = {};

    optimizations.forEach(opt => {
      // Extract notification type from context or metadata
      const type = 'general'; // Would be extracted from actual data

      if (!typeStats[type]) {
        typeStats[type] = { total: 0, engaged: 0 };
      }

      typeStats[type].total++;
      if (opt.opened || opt.clicked) {
        typeStats[type].engaged++;
      }
    });

    return Object.entries(typeStats).map(([type, stats]) => ({
      type,
      engagementRate: stats.total > 0 ? stats.engaged / stats.total : 0,
      total: stats.total,
    }));
  }
);

export const selectOptimalDeliveryTimes = createSelector(
  [selectDeliveryOptimizations],
  optimizations => {
    const hourlyStats: Record<number, { total: number; engaged: number }> = {};

    optimizations.forEach(opt => {
      const hour = new Date(opt.deliveryTime).getHours();

      if (!hourlyStats[hour]) {
        hourlyStats[hour] = { total: 0, engaged: 0 };
      }

      hourlyStats[hour].total++;
      if (opt.opened || opt.clicked) {
        hourlyStats[hour].engaged++;
      }
    });

    return Object.entries(hourlyStats)
      .map(([hour, stats]) => ({
        hour: parseInt(hour),
        engagementRate: stats.total > 0 ? stats.engaged / stats.total : 0,
        total: stats.total,
      }))
      .filter(item => item.total >= 3) // Minimum sample size
      .sort((a, b) => b.engagementRate - a.engagementRate)
      .slice(0, 5); // Top 5 hours
  }
);

export const selectTriggerPerformanceMetrics = createSelector(
  [selectAllActiveTriggers, selectDeliveryOptimizations],
  (triggers, optimizations) => {
    const totalTriggers = triggers.length;
    const recentOptimizations = optimizations.filter(opt => {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
      return new Date(opt.deliveryTime) > oneDayAgo;
    });

    const engagementRate =
      recentOptimizations.length > 0
        ? recentOptimizations.filter(opt => opt.opened || opt.clicked).length /
          recentOptimizations.length
        : 0;

    return {
      totalTriggers,
      activeTriggers: triggers.length,
      recentDeliveries: recentOptimizations.length,
      engagementRate,
      averageEngagementScore:
        recentOptimizations.length > 0
          ? recentOptimizations.reduce(
              (sum, opt) => sum + opt.engagementScore,
              0
            ) / recentOptimizations.length
          : 0,
    };
  }
);

export default contextualTriggersSlice.reducer;
